import {Application, Form, Screen} from '../types';
import {appScreenPath} from './screenPath';

/**
 * A button workflow can do more than report success: it can say where the user
 * should land, what the app should remember, and what the target form starts
 * with. Everything below is read from the workflow's own return value, so no
 * button configuration is involved.
 *
 *   return [
 *       'message'          => ['text' => 'Done', 'type' => 'success'],
 *       'redirectToScreen' => ['screen' => '…', 'filters' => [...], 'context' => [...]],
 *   ];
 *
 *   return [
 *       'message'        => ['text' => 'Saved', 'type' => 'success'],
 *       'redirectToForm' => ['form' => '…', 'record' => 42, 'data' => [...], 'context' => [...]],
 *   ];
 */
export type WorkflowMessageType = 'success' | 'error' | 'info' | 'warning';

export interface WorkflowMessage {
    text: string;
    type: WorkflowMessageType;
}

export type WorkflowRedirect =
    | {
        kind: 'screen';
        screen: string;
        /** Values for the target screen's own context filters. */
        filters: Record<string, unknown>;
        context: Record<string, unknown>;
        replace: boolean;
    }
    | {
        kind: 'form';
        form: string;
        /** Only meaningful for an update form. */
        record: string | number | null;
        /** One-shot prefill, keyed by form field code. */
        data: Record<string, unknown>;
        context: Record<string, unknown>;
        replace: boolean;
    };

const MESSAGE_TYPES: WorkflowMessageType[] = ['success', 'error', 'info', 'warning'];

const asRecord = (value: unknown): Record<string, unknown> =>
    value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};

/** Older scripts return a plain string; both shapes are accepted. */
export const readWorkflowMessage = (responseData: any): WorkflowMessage | null => {
    const message = responseData?.message;
    if (typeof message === 'string' && message.trim() !== '') {
        return {text: message, type: 'success'};
    }
    if (message && typeof message === 'object' && typeof message.text === 'string' && message.text.trim() !== '') {
        const type = MESSAGE_TYPES.includes(message.type) ? message.type as WorkflowMessageType : 'success';
        return {text: message.text, type};
    }
    return null;
};

export const readWorkflowRedirect = (responseData: any): WorkflowRedirect | null => {
    const toScreen = responseData?.redirectToScreen;
    if (toScreen?.screen) {
        return {
            kind: 'screen',
            screen: String(toScreen.screen),
            filters: asRecord(toScreen.filters),
            context: asRecord(toScreen.context),
            replace: !!toScreen.replace,
        };
    }

    const toForm = responseData?.redirectToForm;
    if (toForm?.form) {
        return {
            kind: 'form',
            form: String(toForm.form),
            record: toForm.record ?? null,
            data: asRecord(toForm.data),
            context: asRecord(toForm.context),
            replace: !!toForm.replace,
        };
    }

    return null;
};

const findScreen = (app: Application | null | undefined, screenId: string): Screen | null =>
    (app?.screens as Screen[] | undefined)?.find(s => String(s.id) === screenId) ?? null;

/** Forms live under the screens of the app, as create or update forms. */
const findForm = (app: Application | null | undefined, formId: string): {form: Form; isCreate: boolean} | null => {
    for (const screen of (app?.screens as Screen[] | undefined) ?? []) {
        const create = screen.createForms?.find(f => String(f.id) === formId);
        if (create) return {form: create, isCreate: true};

        const update = screen.updateForms?.find(f => String(f.id) === formId);
        if (update) return {form: update, isCreate: false};
    }
    return null;
};

const formPath = (appId: string, form: Form, isCreate: boolean, recordId: string | number | null): string | null => {
    const nested = form.fields?.some(field => field.type === 'one-to-many');

    if (isCreate) {
        return nested
            ? `/app/${appId}/create-form/${form.id}`
            : `/app/${appId}/create-simple-form/${form.id}`;
    }

    // Editing without a record has nothing to open.
    if (recordId === null || recordId === undefined || recordId === '') return null;

    return nested
        ? `/app/${appId}/update-form/${form.id}/${recordId}`
        : `/app/${appId}/update-simple-form/${form.id}/${recordId}`;
};

export interface RedirectContext {
    app: Application | null | undefined;
    appId: string;
    /** Shown by the page we land on - a toast here would die with this one. */
    notice?: WorkflowMessage | null;
    /** Record to fall back to when the workflow named none (e.g. the open row). */
    fallbackRecordId?: string | number | null;
    setFilter: (code: string, value: string) => void;
    removeFilter: (code: string) => void;
    navigate: (path: string, options?: {replace?: boolean; state?: unknown}) => void;
}

const writeContext = (values: Record<string, unknown>, ctx: RedirectContext): void => {
    Object.entries(values).forEach(([code, value]) => {
        if (value === null || value === undefined || value === '') {
            ctx.removeFilter(code);
            return;
        }
        ctx.setFilter(code, String(value));
    });
};

/**
 * Screens read their scope from the filter context, so narrowing one means
 * writing its filter codes there. Codes the target screen does not declare are
 * dropped - they would sit in the context forever without ever being used.
 */
const writeScreenFilters = (screen: Screen, filters: Record<string, unknown>, ctx: RedirectContext): void => {
    const declared = new Set((screen.filters ?? []).map(f => f.code));
    const known = Object.fromEntries(Object.entries(filters).filter(([code]) => declared.has(code)));

    writeContext(known, ctx);
};

/**
 * Returns true when the redirect was carried out. The context is written before
 * navigating: the target loads its data immediately and must already see it.
 */
export const applyWorkflowRedirect = (redirect: WorkflowRedirect, ctx: RedirectContext): boolean => {
    if (redirect.kind === 'screen') {
        const screen = findScreen(ctx.app, redirect.screen);
        if (!screen) return false;

        writeContext(redirect.context, ctx);
        writeScreenFilters(screen, redirect.filters, ctx);
        ctx.navigate(appScreenPath({id: ctx.appId, screens: ctx.app?.screens}, screen.id), {
            replace: redirect.replace,
            state: ctx.notice ? {notice: ctx.notice} : undefined,
        });
        return true;
    }

    const found = findForm(ctx.app, redirect.form);
    if (!found) return false;

    const path = formPath(ctx.appId, found.form, found.isCreate, redirect.record ?? ctx.fallbackRecordId ?? null);
    if (!path) return false;

    writeContext(redirect.context, ctx);

    // Prefill travels in navigation state on purpose: it is a one-shot value and
    // must not survive a page reload the way stored context does.
    const known = Object.fromEntries(
        Object.entries(redirect.data).filter(([code]) => found.form.fields?.some(field => field.code === code)),
    );
    const state: Record<string, unknown> = {};
    if (Object.keys(known).length > 0) state.prefill = known;
    if (ctx.notice) state.notice = ctx.notice;

    ctx.navigate(path, {
        replace: redirect.replace,
        state: Object.keys(state).length > 0 ? state : undefined,
    });
    return true;
};
